// Instant filtering for the list pages' search boxes (/customers, /plans,
// /templates, /discounts).
//
// Those pages search on the server — a full page load that re-runs the list
// query with its joins and preloads. That stays: it is the only search that
// reaches every row, and it matches fields the page does not show (IDs, slugs,
// statuses). What this adds is the feedback in between: while the merchant
// types, the rows already on the page narrow immediately, with a line saying
// how many match and that Enter searches everything. Submitting shows a
// loading state so the round trip does not feel like a dead click.
//
// Wiring is by attributes on the search form, so a page opts in without any
// page-specific script:
//   data-list-filter="#PlanData"   the table whose tbody rows are filtered
//   data-list-filter-noun="plans"  what the rows are called in the hint line
// Rows are matched on the text of their labelled `td[data-label]` cells, i.e.
// the columns the page itself labels. The action column is skipped: it has no
// label (or, on /customers, an empty one) and holds the row's menu items and
// tooltip copy, which would match every row.

(function () {
  'use strict';

  var HINT_CLASS   = 'lf-hint';
  var HIDDEN_CLASS = 'lf-hidden';

  function init() {
    var forms = document.querySelectorAll('form[data-list-filter]');
    Array.prototype.forEach.call(forms, function (form) {
      if (form.__listFilterBound) return;
      form.__listFilterBound = true;
      setup(form);
    });
  }

  function setup(form) {
    var input = form.querySelector('input[type="text"], input[type="search"]');
    var table = document.querySelector(form.getAttribute('data-list-filter') || '');
    if (!input || !table) return;

    var tbody = table.tBodies[0];
    if (!tbody) return;

    var noun     = form.getAttribute('data-list-filter-noun') || 'rows';
    // Page links only render when there is more than one page.
    var paged    = !!document.querySelector('.pagination a, .digg_pagination a');
    var colspan  = (table.tHead && table.tHead.rows[0]) ? table.tHead.rows[0].cells.length : 1;
    // The term the server already filtered by, when the page is a search
    // result: typing the same thing again should not claim to narrow it.
    var serverTerm = normalize(input.value);
    var frame = null;

    input.setAttribute('autocomplete', 'off');

    input.addEventListener('input', function () {
      if (frame) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(function () {
        frame = null;
        apply(normalize(input.value));
      });
    });

    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && input.value && !serverTerm) {
        e.preventDefault();
        input.value = '';
        apply('');
      }
    });

    form.addEventListener('submit', function () {
      if (!normalize(input.value)) return;   // the page's own script cancels empty submits
      form.classList.add('lf-loading');
      table.classList.add('lf-table-loading');
      showSpinner();
    });

    // A spinner over whichever control the form is showing: the magnifier
    // before a search, the clear (X) once one is active. Placed from the
    // control's own box rather than by page-specific offsets, because the
    // four search boxes size and position that control differently.
    function showSpinner() {
      var control = form.querySelector('.search, .close_search');
      // The positioned box the control sits in — inside the form on /plans
      // and /customers, wrapped around it on /templates and /discounts.
      var parent = control && control.offsetParent;
      if (!parent || parent.querySelector('.lf-spinner')) return;
      var spinner = document.createElement('span');
      spinner.className = 'lf-spinner';
      spinner.style.left = (control.offsetLeft + (control.offsetWidth - 18) / 2) + 'px';
      spinner.style.top = (control.offsetTop + (control.offsetHeight - 18) / 2) + 'px';
      parent.appendChild(spinner);
    }

    function rows() {
      return Array.prototype.filter.call(tbody.rows, function (tr) {
        if (tr.classList.contains(HINT_CLASS)) return false;
        if (tr.querySelector('td.no-data')) return false;   // empty-state rows
        return tr.cells.length > 0;
      });
    }

    function textOf(tr) {
      if (tr.__lfText === undefined) {
        var cells = tr.querySelectorAll('td[data-label]:not([data-label=""])');
        var parts = [];
        Array.prototype.forEach.call(cells, function (td) { parts.push(td.textContent || ''); });
        if (!parts.length) parts.push(tr.textContent || '');
        tr.__lfText = normalize(parts.join(' ')).toLowerCase();
      }
      return tr.__lfText;
    }

    function apply(query) {
      var all = rows();
      var q = query.toLowerCase();

      if (!q || q === serverTerm.toLowerCase()) {
        all.forEach(function (tr) { show(tr); });
        table.classList.remove('lf-filtering');
        removeHint();
        return;
      }

      var words = q.split(' ');
      var shown = 0;
      all.forEach(function (tr) {
        var text = textOf(tr);
        var match = words.every(function (w) { return text.indexOf(w) !== -1; });
        if (match) { show(tr); shown++; } else { hide(tr); }
      });

      table.classList.add('lf-filtering');
      renderHint(shown, all.length, query);
    }

    function show(tr) {
      tr.classList.remove(HIDDEN_CLASS);
      tr.style.display = '';
    }

    function hide(tr) {
      tr.classList.add(HIDDEN_CLASS);
      tr.style.display = 'none';
    }

    function renderHint(shown, total, query) {
      var tr = tbody.querySelector('tr.' + HINT_CLASS);
      if (!tr) {
        tr = document.createElement('tr');
        tr.className = HINT_CLASS;
        var td = document.createElement('td');
        td.colSpan = colspan;
        tr.appendChild(td);
        tbody.insertBefore(tr, tbody.firstChild);
      }
      var scope = paged ? ' on this page' : '';
      var text;
      if (shown === 0) {
        text = 'No ' + noun + scope + ' match “' + query + '”.';
      } else {
        text = 'Showing ' + shown + ' of ' + total + ' ' + noun + scope + ' matching “' + query + '”.';
      }
      tr.firstChild.innerHTML =
        '<span class="lf-hint-text"></span> ' +
        '<span class="lf-hint-action">Press <kbd>Enter</kbd> to search all ' + escHtml(noun) +
        (paged ? '' : ' by ID, status and more') + '.</span>';
      tr.firstChild.querySelector('.lf-hint-text').textContent = text;
    }

    function removeHint() {
      var tr = tbody.querySelector('tr.' + HINT_CLASS);
      if (tr) tr.parentNode.removeChild(tr);
    }
  }

  function normalize(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  function escHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  document.addEventListener('turbolinks:load', init);
  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
}());
